import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { X, Activity, Wind, Brain, Heart, Moon, Utensils, Thermometer, Bed, Star } from 'lucide-react';
export function OpeningHook() {
  return <section className="py-[80px] px-6 bg-cream" data-id="element-275">
      <div className="max-w-[720px] mx-auto text-center space-y-12" data-id="element-276">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="space-y-8" data-id="element-277">
          <h2 className="text-5xl md:text-7xl font-serif text-navy-800" data-id="element-278">
            It's 11PM.
          </h2>

          <div className="space-y-6 text-xl md:text-2xl text-navy-800/80 font-serif leading-relaxed" data-id="element-279">
            <p data-id="element-280">You're exhausted.</p>
            <p data-id="element-281">Your eyes are heavy. Your day was long. You deserve sleep.</p>
            <p data-id="element-282">But the moment your body hits that mattress...</p>
            <p className="text-teal-500 font-bold" data-id="element-283">
              Your lower back lights up like a bonfire.
            </p>
          </div>

          <div className="space-y-4 text-[17px] text-body max-w-2xl mx-auto leading-relaxed" data-id="element-284">
            <p data-id="element-285">
              You shift to the left. Still there. You roll to the right. Worse.
              You stack the pillows. Rearrange them. Try lying flat. Try curling
              up.
            </p>
            <p className="font-bold text-navy-800" data-id="element-286">Nothing works.</p>
            <p data-id="element-287">
              And somewhere between minute 20 and minute 40 of this exhausting
              position-switching ritual...
            </p>
            <p data-id="element-288">A thought creeps in that terrifies you:</p>
          </div>

          <div className="py-8" data-id="element-289">
            <p className="text-3xl md:text-4xl font-serif italic text-gold" data-id="element-290">
              "Is this just my life now?"
            </p>
          </div>

          <div className="space-y-6 text-[17px] text-body leading-relaxed" data-id="element-291">
            <p data-id="element-292">
              If you felt something reading that — if your chest tightened even
              slightly — then this letter was written for you.
            </p>
            <p data-id="element-293">Because what you just read isn't a dramatic story.</p>
            <p className="font-bold text-navy-800" data-id="element-294">
              It's Tuesday night for over 800 million people worldwide.
            </p>
            <p data-id="element-295">And the worst part?</p>
            <p data-id="element-296">It's not because they haven't tried.</p>
            <p className="text-2xl font-serif text-teal-500" data-id="element-297">
              They've tried everything.
            </p>
          </div>
        </motion.div>
      </div>
    </section>;
}
const failedAttempts = [{
  title: "You've tried the heating pad.",
  desc: 'It helps for 7 minutes. Then the heat fades. And the pain is still there. Waiting.'
}, {
  title: "You've tried the painkillers.",
  desc: "They take the edge off — until they don't. And now you're worried about your stomach. Your liver. Your dependency."
}, {
  title: "You've tried the YouTube stretches.",
  desc: 'Some guy in yoga pants promises relief in 5 minutes. You follow along. Feel okay during. Then wake up at 3AM worse than before.'
}, {
  title: "You've tried chiropractic adjustments.",
  desc: '$80 a session. Temporary relief. But the money runs out before the pain does.'
}, {
  title: "You've tried just ignoring it.",
  desc: 'Pushing through. Being tough about it. Until the pain started stealing things from you.'
}];
export function PainAmplification() {
  return <section className="py-[80px] px-6 bg-white border-y border-navy-800/5" data-id="element-298">
      <div className="max-w-[800px] mx-auto" data-id="element-299">
        <div className="space-y-6 mb-16" data-id="element-300">
          {failedAttempts.map((item, i) => <motion.div key={i} initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className="flex gap-4 items-start bg-cream p-6 rounded-[16px] border border-navy-800/5 shadow-[0_4px_24px_rgba(0,0,0,0.03)]" data-id="element-301">
              <div className="mt-1 bg-red-500/10 p-1.5 rounded-full shrink-0" data-id="element-302">
                <X className="w-5 h-5 text-red-500" data-id="element-303" />
              </div>
              <div data-id="element-304">
                <h3 className="text-[18px] font-bold text-navy-800 mb-2" data-id="element-305">
                  {item.title}
                </h3>
                <p className="text-body leading-relaxed" data-id="element-306">{item.desc}</p>
              </div>
            </motion.div>)}
        </div>

        <div className="text-center space-y-8 mb-16 max-w-[640px] mx-auto" data-id="element-307">
          <p className="text-[17px] text-body leading-relaxed" data-id="element-308">
            Your sleep. Your mornings. Your patience with your kids. Your
            enjoyment of a simple walk. Your confidence that tomorrow will be
            different.
          </p>

          <p className="text-2xl font-serif text-teal-500 italic" data-id="element-309">
            Let me ask you something honest:
          </p>
        </div>

        <div className="bg-cream border-l-4 border-gold p-8 md:p-12 rounded-r-[16px] space-y-8 mb-16 shadow-sm" data-id="element-310">
          <p className="text-xl md:text-2xl font-serif text-navy-800 leading-relaxed" data-id="element-311">
            How many nights have you lain awake, doing mental math on how many
            hours of sleep you might still get if you fall asleep RIGHT NOW?
          </p>
          <p className="text-xl md:text-2xl font-serif text-navy-800 leading-relaxed" data-id="element-312">
            How many mornings have you woken up already dreading bedtime?
          </p>
          <p className="text-xl md:text-2xl font-serif text-navy-800 leading-relaxed" data-id="element-313">
            How many times have you told yourself "I'll see a doctor about this"
            and then didn't — because deep down you're afraid of what they'll
            say?
          </p>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center space-y-6" data-id="element-314">
          <h3 className="text-3xl md:text-5xl font-serif text-navy-800 leading-tight" data-id="element-315">
            You are not weak.
            <br data-id="element-316" />
            You are not broken.
            <br data-id="element-317" />
            You are not imagining it.
          </h3>
          <p className="text-[17px] text-body" data-id="element-318">
            You are simply using the wrong approach.
          </p>
          <p className="text-2xl font-bold text-teal-500" data-id="element-319">
            And tonight, that changes.
          </p>
        </motion.div>
      </div>
    </section>;
}
export function PainLoopReveal() {
  return <section className="py-[80px] px-6 bg-cream" data-id="element-320">
      <div className="max-w-[720px] mx-auto text-center space-y-6" data-id="element-321">
        <h2 className="text-3xl md:text-5xl font-serif text-navy-800" data-id="element-322">
          Here's what no one has told you.
        </h2>
        <p className="text-[17px] text-body max-w-2xl mx-auto leading-relaxed" data-id="element-323">
          And it's the most important thing you will read about your back pain:
        </p>
        <p className="text-2xl font-bold text-teal-500" data-id="element-324">
          Your problem is not in your back.
        </p>
        <p className="text-[17px] text-body" data-id="element-325">At least — not entirely.</p>
        <p className="text-[17px] text-body max-w-2xl mx-auto leading-relaxed" data-id="element-326">
          Yes, there's inflammation. Yes, there's compression. Yes, your muscles
          are tight and your discs are angry. But the reason nothing has
          provided lasting relief is this:
        </p>
        <p className="text-3xl font-serif text-navy-800 italic py-8" data-id="element-327">
          Your nervous system is stuck in a pain loop.
        </p>

        <div className="space-y-6 text-[17px] text-body max-w-3xl mx-auto text-center leading-relaxed" data-id="element-328">
          <p data-id="element-329">This is why it gets worse at night.</p>
          <p data-id="element-330">
            During the day, movement, distraction, and cortisol naturally
            suppress the pain signal.
          </p>
          <p className="text-xl text-navy-800 font-medium" data-id="element-331">
            But the moment you lie down in silence and stillness?
          </p>
          <p data-id="element-332">
            Your nervous system has your full attention. And it uses every
            second of it.
          </p>
          <p data-id="element-333">
            Every solution you've tried has attacked ONE point in this loop.
          </p>

          <div className="bg-white p-6 rounded-[16px] border border-navy-800/5 shadow-sm my-8" data-id="element-334">
            <p className="font-mono text-sm text-body" data-id="element-335">
              Painkillers: mute the signal.
              <br data-id="element-336" />
              Stretching: target the muscle.
              <br data-id="element-337" />
              Heat pads: address the inflammation.
            </p>
          </div>

          <p className="text-xl font-bold text-navy-800" data-id="element-338">
            But the loop has four entry points.
          </p>
          <p data-id="element-339">And unless you interrupt ALL of them simultaneously?</p>
          <p className="text-2xl font-serif text-teal-500" data-id="element-340">
            The loop restarts. Every. Single. Night.
          </p>
        </div>
      </div>
    </section>;
}
const pillars = [{
  icon: Activity,
  title: 'Pilates Core Science',
  desc: 'Mechanically decompresses the lumbar spine safely.'
}, {
  icon: Heart,
  title: 'Yin Yoga Fascial Release',
  desc: 'Releases deep connective tissue tension holding the pain.'
}, {
  icon: Brain,
  title: 'Somatic Re-patterning',
  desc: "Teaches the nervous system to exit 'threat mode'."
}, {
  icon: Wind,
  title: 'Vagus Nerve Activation',
  desc: 'Uses specific breath patterns as a biological pain off-switch.'
}];
export function MechanismReveal() {
  return <section className="py-[80px] px-6 bg-white border-t border-navy-800/5" data-id="element-341">
      <div className="max-w-[880px] mx-auto" data-id="element-342">
        <div className="text-center space-y-6 mb-16" data-id="element-343">
          <h2 className="text-3xl md:text-5xl font-serif text-navy-800 leading-tight" data-id="element-344">
            What if you could break all four points of the loop in under 12
            minutes?
          </h2>
          <p className="text-[17px] text-body max-w-3xl mx-auto leading-relaxed" data-id="element-345">
            Not with medication. Not with expensive equipment. Not with a
            45-minute yoga class you don't have energy for at 11PM.
          </p>
          <p className="text-[17px] text-body leading-relaxed" data-id="element-346">
            But with a precise sequence — developed from the intersection of
            four evidence-based disciplines:
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16" data-id="element-347">
          {pillars.map((pillar, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className="bg-cream border border-navy-800/5 p-6 rounded-[16px] text-center shadow-[0_4px_24px_rgba(0,0,0,0.03)]" data-id="element-348">
              <div className="w-12 h-12 mx-auto bg-teal-400/10 rounded-full flex items-center justify-center mb-4 text-teal-500" data-id="element-349">
                <pillar.icon className="w-6 h-6" data-id="element-350" />
              </div>
              <h3 className="text-lg font-bold text-navy-800 mb-2" data-id="element-351">
                {pillar.title}
              </h3>
              <p className="text-[14px] text-body" data-id="element-352">{pillar.desc}</p>
            </motion.div>)}
        </div>

        <div className="text-center space-y-8" data-id="element-353">
          <p className="text-[17px] text-body max-w-2xl mx-auto leading-relaxed" data-id="element-354">
            When these four work together in a specific sequence... Your muscles
            release their guarding reflex. Your inflammation response drops.
            Your nervous system exits "threat mode." Your pain perception
            normalizes.
          </p>
          <p className="text-2xl font-serif text-navy-800 italic" data-id="element-355">
            And your body finally gets the signal it's been desperately waiting
            for: <br data-id="element-356" />
            <span className="text-teal-500 font-bold not-italic" data-id="element-357">
              It's safe to sleep now.
            </span>
          </p>

          <div className="mt-12 inline-block" data-id="element-358">
            <p className="text-[11px] text-teal-500 uppercase tracking-[0.15em] font-bold mb-3" data-id="element-359">
              This combination is what we call:
            </p>
            <div className="bg-navy-800 px-8 py-6 rounded-[16px] shadow-lg" data-id="element-360">
              <h3 className="text-3xl md:text-4xl font-serif text-gold" data-id="element-361">
                THE SOMATIC FUSION METHOD™
              </h3>
            </div>
            <p className="mt-6 text-body max-w-xl mx-auto leading-relaxed text-[15px]" data-id="element-362">
              The first and only protocol specifically designed to interrupt the
              nighttime pain loop at all four points — in 12 minutes — from the
              floor of your bedroom.
            </p>
          </div>
        </div>
      </div>
    </section>;
}
export function ProductIntro() {
  return <section className="py-[80px] px-6 bg-cream border-t border-navy-800/5" data-id="element-363">
      <div className="max-w-[880px] mx-auto grid md:grid-cols-2 gap-12 items-center" data-id="element-364">
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true
      }} className="relative" data-id="element-365">
          <img src="https://cdn.magicpatterns.com/uploads/c3GNTfJySRtbVSMvFSSmJW/The_11pm_Spine_Reset_eBook_Cover.png" alt="The 11PM Spine Reset" className="relative z-10 w-full max-w-md mx-auto rounded-[16px] shadow-[0_8px_32px_rgba(0,0,0,0.1)] border border-navy-800/5" data-id="element-366" />
        </motion.div>

        <div className="space-y-8" data-id="element-367">
          <div data-id="element-368">
            <p className="text-teal-500 font-bold tracking-[0.15em] uppercase text-[11px] mb-4" data-id="element-369">
              Introducing
            </p>
            <h2 className="text-4xl md:text-5xl font-serif text-navy-800 leading-tight mb-4" data-id="element-370">
              The 11PM Spine Reset
            </h2>
            <p className="text-[17px] text-body font-medium leading-relaxed" data-id="element-371">
              The 12-Minute Holistic Protocol That Stops Chronic Back Pain &
              Restores Deep Sleep — Tonight
            </p>
          </div>

          <div className="space-y-4 text-[15px] text-body leading-relaxed" data-id="element-372">
            <p data-id="element-373">
              This is not a stretching guide. This is not a yoga book. This is
              not another "try these 5 exercises" PDF.
            </p>
            <p data-id="element-374">
              This is a complete, sequenced, science-backed nightly protocol —
              built specifically for people whose back pain peaks at night and
              steals their sleep.
            </p>
          </div>

          <ul className="space-y-3 bg-white p-6 rounded-[16px] border border-navy-800/5 shadow-sm" data-id="element-375">
            <li className="flex items-start gap-3" data-id="element-376">
              <span className="text-teal-500 mt-0.5" data-id="element-377">✦</span>
              <span className="text-navy-800 text-[15px] font-medium" data-id="element-378">
                It works if you're 28 or 78.
              </span>
            </li>
            <li className="flex items-start gap-3" data-id="element-379">
              <span className="text-teal-500 mt-0.5" data-id="element-380">✦</span>
              <span className="text-navy-800 text-[15px] font-medium" data-id="element-381">
                It works if you've had back pain for 3 weeks or 13 years.
              </span>
            </li>
            <li className="flex items-start gap-3" data-id="element-382">
              <span className="text-teal-500 mt-0.5" data-id="element-383">✦</span>
              <span className="text-navy-800 text-[15px] font-medium" data-id="element-384">
                It works if you've tried everything else and feel hopeless.
              </span>
            </li>
          </ul>

          <p className="text-[17px] font-serif text-teal-600 italic leading-relaxed" data-id="element-385">
            Because it's the first approach that doesn't attack one point of the
            loop. It destroys the entire loop. Every night.
          </p>
        </div>
      </div>
    </section>;
}
const chapters = [{
  num: '1',
  icon: Moon,
  title: 'The Midnight Threshold',
  tagline: "Why Your Pain Peaks At Night — And Why That's Actually Good News",
  desc: "You'll finally understand the exact biological reason your pain is worst at bedtime. And why understanding this mechanism is the first step to ending it permanently."
}, {
  num: '2',
  icon: Wind,
  title: 'The Vagus Key',
  tagline: 'The SOS Breathing Pattern That Switches Pain Off In 90 Seconds',
  desc: "Your body has a built-in pain off-switch. It's called the vagus nerve. This chapter gives you the exact 4-7-8 protocol that down-regulates your pain response before you even begin."
}, {
  num: '3',
  icon: Activity,
  title: 'The 7-Minute Somatic Sequence',
  tagline: 'The Floor Protocol That Decompresses Your Spine',
  desc: "The heart of the system. A precisely ordered series of movements that mechanically decompresses your lumbar spine while simultaneously releasing the nervous system's guarding reflex."
}, {
  num: '4',
  icon: Utensils,
  title: 'Nutritional First Aid',
  tagline: 'What You Eat Is Either Fueling Or Fighting Your Back Pain',
  desc: 'The exact anti-inflammatory food framework, the 7-day spine healing meal plan, and the nightly Spine Reset Tonic recipe that reduces your inflammatory load while you sleep.'
}, {
  num: '5',
  icon: Thermometer,
  title: 'Temperature & Touch Therapy',
  tagline: 'The Contrast Cycling Protocol + Self-Acupressure Map',
  desc: 'Learn the exact heat vs cold decision guide, the 5-minute self-massage protocol using a tennis ball, and the 5-point acupressure map that targets lumbar pain relief.'
}, {
  num: '6',
  icon: Bed,
  title: 'The Sleep-Ready Environment',
  tagline: 'Transform Your Bedroom Into A Spine Healing Sanctuary',
  desc: "The 3-minute Pain Dissolve Meditation. The Spine-Safe Sleep Position Guide. Because the work doesn't end when you finish the sequence."
}];
export function ChaptersGrid() {
  return <section className="py-[80px] px-6 bg-white" data-id="element-386">
      <div className="max-w-[1100px] mx-auto" data-id="element-387">
        <div className="text-center mb-16" data-id="element-388">
          <h2 className="text-teal-400 uppercase text-[11px] tracking-[0.15em] font-bold mb-4" data-id="element-389">
            INSIDE THE GUIDE
          </h2>
          <h3 className="text-4xl md:text-5xl font-serif text-navy-800 mb-4" data-id="element-390">
            What's Inside
          </h3>
          <div className="w-16 h-1 bg-gold mx-auto rounded-full" data-id="element-391" />
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-392">
          {chapters.map((ch, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className="bg-white border border-navy-800/10 rounded-[16px] p-8 relative overflow-hidden shadow-[0_4px_24px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_32px_rgba(0,0,0,0.08)] transition-shadow" data-id="element-393">
              <div className="absolute top-0 right-0 text-9xl font-serif text-navy-800/[0.03] font-black -mt-8 -mr-4 pointer-events-none" data-id="element-394">
                {ch.num}
              </div>
              <div className="relative z-10" data-id="element-395">
                <div className="w-12 h-12 bg-teal-400/10 rounded-xl flex items-center justify-center text-teal-500 mb-6" data-id="element-396">
                  <ch.icon className="w-6 h-6" data-id="element-397" />
                </div>
                <h4 className="text-[11px] font-bold text-teal-500 tracking-widest uppercase mb-2" data-id="element-398">
                  Chapter {ch.num}
                </h4>
                <h5 className="text-2xl font-serif text-navy-800 mb-3" data-id="element-399">
                  {ch.title}
                </h5>
                <p className="text-navy-800/70 font-medium italic mb-4 text-[14px]" data-id="element-400">
                  {ch.tagline}
                </p>
                <p className="text-body text-[14px] leading-relaxed" data-id="element-401">
                  {ch.desc}
                </p>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}
const testimonials = [{
  quote: "I've had lower back pain for 11 years. I tried everything. I did the sequence three nights in a row and slept through the night for the first time since 2019. I genuinely cried.",
  name: 'Margaret T.',
  details: '58, London',
  image: 'https://images.unsplash.com/photo-1605405748313-a416a1b84491?w=200&h=200&fit=crop&crop=faces&auto=format'
}, {
  quote: "I was skeptical. I'm a physical therapist and I've seen a lot of back pain 'solutions' that are just recycled content. This is different. The vagus nerve breathing protocol alone is worth 10x the price.",
  name: 'Dr. James R.',
  details: 'Physical Therapist, Toronto',
  image: 'https://randomuser.me/api/portraits/men/52.jpg'
}, {
  quote: "My husband bought this for me because I was waking him up every night shifting around. We're both sleeping better now. Our marriage thanks you.",
  name: 'Sandra K.',
  details: '47, Sydney',
  image: 'https://randomuser.me/api/portraits/women/45.jpg'
}, {
  quote: 'I work a desk job and my lower back has been a disaster for 3 years. The Desk-Side Mini Reset bonus alone changed my workday completely. I do it every 90 minutes and the afternoon pain spike is basically gone.',
  name: 'Carlos M.',
  details: '34, Miami',
  image: 'https://randomuser.me/api/portraits/men/32.jpg'
}, {
  quote: 'At 71, I thought back pain was just part of getting older. The acupressure map in Chapter 5 is something I use every single night now. The difference is remarkable.',
  name: 'Dorothy H.',
  details: '71, Manchester',
  image: 'https://images.unsplash.com/photo-1581579438747-1dc8d17bbce4?w=200&h=200&fit=crop&crop=faces'
}];
export function Testimonials() {
  return <section className="py-[80px] px-6 bg-cream border-y border-navy-800/5" data-id="element-402">
      <div className="max-w-[1100px] mx-auto" data-id="element-403">
        <div className="text-center mb-16" data-id="element-404">
          <h2 className="text-4xl md:text-5xl font-serif text-navy-800 mb-4" data-id="element-405">
            What Real People Are Saying
          </h2>
          <div className="flex justify-center gap-1 text-gold mb-4" data-id="element-406">
            {[...Array(5)].map((_, i) => <Star key={i} className="w-6 h-6 fill-current" data-id="element-407" />)}
          </div>
          <p className="text-body text-[17px]" data-id="element-408">
            Over 47,000+ people have used the Spine Reset protocol.
          </p>
        </div>

        <div className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6" data-id="element-409">
          {testimonials.map((t, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className="break-inside-avoid bg-white border border-navy-800/10 p-8 rounded-[16px] shadow-[0_4px_24px_rgba(0,0,0,0.04)]" data-id="element-410">
              <div className="flex gap-1 text-gold mb-4" data-id="element-411">
                {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-current" data-id="element-412" />)}
              </div>
              <p className="text-body italic mb-6 leading-relaxed text-[15px]" data-id="element-413">
                "{t.quote}"
              </p>
              <div className="flex items-center gap-4" data-id="element-414">
                <img src={t.image} alt={`Portrait of ${t.name}`} className="w-14 h-14 rounded-full object-cover border-2 border-teal-400/30 shadow-sm shrink-0" loading="lazy" data-id="element-415" />
                <div data-id="element-416">
                  <p className="font-bold text-navy-800" data-id="element-417">{t.name}</p>
                  <p className="text-[13px] text-teal-600" data-id="element-418">{t.details}</p>
                </div>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}
export function StrongClose() {
  return <section className="py-[80px] px-6 bg-navy-800 border-b border-white/10" data-id="element-419">
      <div className="max-w-[720px] mx-auto text-center space-y-8" data-id="element-420">
        <h2 className="text-4xl md:text-5xl font-serif text-white leading-tight" data-id="element-421">
          You've been living with this long enough.
        </h2>

        <div className="space-y-6 text-[17px] text-white/80 leading-relaxed" data-id="element-422">
          <p data-id="element-423">
            You know the routine. The shifting. The sighing. The 3AM ceiling
            stare. The morning you start already tired.
          </p>
          <p data-id="element-424">You've tried the things they told you to try. And here you are.</p>
          <p data-id="element-425">Still reading. Still searching. Still in pain.</p>
          <p className="text-xl font-serif text-white italic" data-id="element-426">
            Which means you know, somewhere deep down, that the answer hasn't
            arrived yet.
          </p>
          <p className="text-2xl font-bold text-teal-400" data-id="element-427">Until now.</p>
        </div>

        <div className="bg-white/5 p-8 rounded-[16px] border border-white/10 my-12 text-left space-y-4 shadow-lg" data-id="element-428">
          <p className="text-white/90 text-[16px] leading-relaxed" data-id="element-429">
            The 11PM Spine Reset is not a miracle. It's a method.
          </p>
          <p className="text-white/90 text-[16px] leading-relaxed" data-id="element-430">
            A precise, sequenced, science-backed method that attacks your pain
            where it actually lives — in the nervous system loop that runs on
            autopilot every night while you're trying to sleep.
          </p>
          <p className="text-white font-bold text-[16px] leading-relaxed" data-id="element-431">
            12 minutes. A yoga mat. A tennis ball. And the knowledge that
            tonight could genuinely be different.
          </p>
        </div>

        <div className="space-y-4" data-id="element-432">
          <p className="text-2xl font-serif text-white" data-id="element-433">
            You deserve to sleep. You deserve to wake up without pain. You
            deserve your life back.
          </p>
          <p className="text-xl text-teal-400 font-bold" data-id="element-434">
            And it starts tonight.
          </p>
          <p className="text-white/60 text-[15px]" data-id="element-435">
            For less than the cost of one chiropractic co-pay.
          </p>
        </div>
      </div>
    </section>;
}